import { useMemo } from "react";
import { isSameDay, startOfDay } from "date-fns";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { useForm, useStore } from "@tanstack/react-form";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { RequiredInput } from "../common/required-input";
import { Separator } from "@/components/ui/separator";
import { DatePicker } from "@/components/common/date-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  APPOINTMENT_CALENDAR_STATUS_OPTIONS,
  APPOINTMENT_SERVICE_ROW_GRID,
} from "@/constants/appointments/appointments-constants";
import {
  editAppointmentSchema,
  type EditAppointmentFormValues,
} from "@/zod-schemas/appointments/edit-appointment-schema";
import {
  EMPTY_APPOINTMENT_SERVICE_LINE,
  getEditAppointmentFormValues,
} from "@/default-form-values/appointments/default-appointment-form-values";
import { toAppointmentUpdate } from "@/mappers/appointments/appointment-form-to-request";
import {
  useGetAvailability,
  useUpdateAppointment,
} from "@/hooks/appointments/use-appointments";
import {
  useGetStaffPaginated,
  useGetStaffServicesSummary,
} from "@/hooks/staff/use-staff";
import { DURATION_MINUTES } from "@/constants/services/services-constants";
import type {
  CalendarEditableAppointment,
  RowConflict,
} from "@/types/appointments/appointment-form-types";
import {
  availableTimesForLine,
  getRowConflict,
  getServiceOptions,
  lineOverlapsSibling,
  serviceNameFor,
  slotRangesForStaff,
  staffLabel,
  sumLinePrices,
  withExistingDuration,
  withExistingService,
  withExistingStartTime,
} from "@/lib/appointments/appointment-form-utils";
import { durationFromMinutes, isoDate, isoTime, toIsoDate } from "@/lib/utils";
import { useSalonToday } from "@/hooks/me/use-salon";
import {
  APPOINTMENT_TOAST_DURATION_MS,
  appointmentToastMessage,
} from "@/lib/appointments/appointment-toast";
import { useIntl } from "react-intl";

interface EditAppointmentDialogProps {
  appointment: CalendarEditableAppointment;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdated?: (savedDate: Date) => void;
}

export function EditAppointmentDialog({
  appointment,
  open,
  onOpenChange,
  onUpdated,
}: EditAppointmentDialogProps) {
  const intl = useIntl();
  const today = useSalonToday();
  const originalDate = useMemo(
    () => isoDate(appointment.appointment_date),
    [appointment.appointment_date]
  );
  const schema = useMemo(
    () => editAppointmentSchema(originalDate, today),
    [originalDate, today]
  );
  const updateAppointment = useUpdateAppointment();
  const { data: staffResponse } = useGetStaffPaginated({
    active_only: true,
    size: 100,
  });
  const {
    data: servicesSummary,
    isError: servicesSummaryIsError,
  } = useGetStaffServicesSummary();
  const staffList = staffResponse?.items ?? [];

  const form = useForm({
    defaultValues: getEditAppointmentFormValues(appointment),
    validators: {
      onSubmit: schema,
    },
    onSubmit: async ({ value }) => {
      const validated = schema.parse(value);
      const action =
        validated.status !== appointment.status
          ? validated.status
          : "updated";
      try {
        const updated = await updateAppointment.mutateAsync({
          id: appointment.id,
          data: toAppointmentUpdate(validated),
        });
        toast.success(
          appointmentToastMessage({
            intl,
            action,
            variant: "success",
            clientName: updated.client_name ?? appointment.client_name,
            appointmentDate: updated.appointment_date,
          }),
          { duration: APPOINTMENT_TOAST_DURATION_MS }
        );
      } catch {
        toast.error(
          appointmentToastMessage({
            intl,
            action,
            variant: "error",
            clientName: appointment.client_name,
            appointmentDate: validated.appointment_date,
          }),
          { duration: APPOINTMENT_TOAST_DURATION_MS }
        );
        return;
      }
      onUpdated?.(validated.appointment_date);
      onOpenChange(false);
    },
  });

  const formDate = useStore(form.store, (state) => state.values.appointment_date);
  // exclude_appointment_id so this booking's own slots stay free on the form date.
  const {
    data: availabilityResponse,
    isError: availabilityIsError,
  } = useGetAvailability(formDate, appointment.id);
  const formDateStr = toIsoDate(formDate);

  // Ignore a previous day's payload while the new date is still loading.
  const rosterStaff =
    availabilityResponse?.date === formDateStr
      ? availabilityResponse.staff
      : [];
  const rosterIds = new Set(
    rosterStaff.map((entry) => Number(entry.staff_id))
  );
  const stylistOptions = staffList.filter((member) => rosterIds.has(member.id));
  const noStaffWorking =
    availabilityResponse?.date === formDateStr &&
    !availabilityIsError &&
    rosterStaff.length === 0;
  const availabilityReady =
    availabilityResponse?.date === formDateStr && !availabilityIsError;
  const servicesSummaryReady =
    servicesSummary !== undefined && !servicesSummaryIsError;

  const conflictForLine = (
    line: EditAppointmentFormValues["services"][number],
    index: number,
    services: EditAppointmentFormValues["services"]
  ): RowConflict => {
    const conflict = getRowConflict({
      line,
      availabilityReady,
      rosterIds,
      serviceOptions: getServiceOptions(line.staff_id, servicesSummary),
      slotRanges: slotRangesForStaff(line.staff_id, rosterStaff),
      servicesSummaryReady,
    });
    if (conflict) return conflict;
    if (lineOverlapsSibling(line, index, services)) return "overlap";
    return null;
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        onOpenChange(isOpen);
        if (!isOpen) {
          form.reset();
        }
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Edit Appointment</DialogTitle>
        </DialogHeader>
        <Separator />

        <form
          className="mb-4"
          id="edit-appointment-form"
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
        >
          <FieldGroup>
            <form.Field
              name="client_id"
              children={() => (
                <Field>
                  <FieldLabel htmlFor="client_name">
                    Client Name
                    <RequiredInput />
                  </FieldLabel>
                  {/* Client cannot be changed on edit; PUT body omits client_id. */}
                  <Input
                    id="client_name"
                    value={appointment.client_name ?? ""}
                    disabled
                  />
                </Field>
              )}
            />

            <form.Field
              name="appointment_date"
              children={(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;
                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>
                      Date
                      <RequiredInput />
                    </FieldLabel>
                    <DatePicker
                      id={field.name}
                      date={field.state.value}
                      dateFormat="long"
                      // Today-or-future, plus the original past day so a past booking can stay.
                      calendarDisabled={(day) =>
                        startOfDay(day) < today &&
                        !isSameDay(day, originalDate)
                      }
                      onDateChange={(date) => {
                        if (!date) return;
                        field.handleChange(date);
                      }}
                    />
                    {isInvalid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                  </Field>
                );
              }}
            />

            <Separator />

            <form.Field
              name="services"
              mode="array"
              children={(linesField) => (
                <>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold">Appointment</p>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label="Add service"
                      onClick={() =>
                        linesField.pushValue({
                          ...EMPTY_APPOINTMENT_SERVICE_LINE,
                        })
                      }
                    >
                      <Plus />
                    </Button>
                  </div>

                  {linesField.state.value.map((_, index) => (
                    // Nested field changes do not re-render the array map — Subscribe the
                    // whole services list so overlap on this row updates when a sibling changes.
                    <form.Subscribe
                      key={index}
                      selector={(state) => state.values.services}
                      children={(services) => {
                        const line = services[index];
                        if (!line) return null;
                        const selectedStaffId = line.staff_id;
                        const selectedServiceId = line.service_id;
                        const serviceOptions = getServiceOptions(
                          selectedStaffId,
                          servicesSummary
                        );
                        const slotRanges = slotRangesForStaff(
                          selectedStaffId,
                          rosterStaff
                        );
                        const availableTimes = availableTimesForLine(
                          line,
                          slotRanges
                        );
                        const timeOptions = withExistingStartTime(
                          availableTimes,
                          line.start_time
                        );
                        const rowStylist = staffList.find(
                          (member) => member.id === selectedStaffId
                        );
                        const rowStylistName = rowStylist
                          ? staffLabel(rowStylist)
                          : "This stylist";
                        const rowStylistOptions =
                          rowStylist &&
                          !stylistOptions.some(
                            (member) => member.id === selectedStaffId
                          )
                            ? [...stylistOptions, rowStylist]
                            : stylistOptions;
                        const rowServiceOptions = withExistingService(
                          serviceOptions,
                          selectedServiceId,
                          {
                            service_id: selectedServiceId,
                            service_name: serviceNameFor(
                              selectedServiceId,
                              servicesSummary,
                              appointment
                            ),
                            category_name: null,
                            duration_minutes: line.duration_minutes,
                            price: line.price,
                          }
                        );
                        const durationOptions = withExistingDuration(
                          DURATION_MINUTES,
                          line.duration_minutes
                        );
                        const conflict = conflictForLine(
                          line,
                          index,
                          services
                        );
                        const noServicesAssigned =
                          selectedStaffId !== 0 &&
                          servicesSummaryReady &&
                          serviceOptions.length === 0 &&
                          conflict !== "stylist" &&
                          conflict !== "service";
                        const noTimesAvailable =
                          selectedStaffId !== 0 &&
                          availabilityReady &&
                          availableTimes.length === 0 &&
                          conflict !== "stylist" &&
                          conflict !== "service" &&
                          conflict !== "time" &&
                          conflict !== "duration" &&
                          conflict !== "overlap";

                        return (
                          <div className={APPOINTMENT_SERVICE_ROW_GRID}>
                            <form.Field
                              name={`services[${index}].staff_id`}
                              children={(field) => {
                                const isInvalid =
                                  field.state.meta.isTouched &&
                                  !field.state.meta.isValid;
                                const showConflict = conflict === "stylist";
                                return (
                                  <Field data-invalid={isInvalid || showConflict}>
                                    <FieldLabel htmlFor={field.name}>
                                      Stylist
                                      <RequiredInput />
                                    </FieldLabel>
                                    <Select
                                      value={
                                        field.state.value
                                          ? String(field.state.value)
                                          : undefined
                                      }
                                      onValueChange={(value) => {
                                        const next = Number(value);
                                        if (next === field.state.value) return;
                                        field.handleChange(next);
                                        const offered = getServiceOptions(
                                          next,
                                          servicesSummary
                                        ).find(
                                          (item) =>
                                            item.service_id ===
                                            line.service_id
                                        );
                                        if (
                                          offered?.duration_minutes != null &&
                                          offered.price != null
                                        ) {
                                          form.setFieldValue(
                                            `services[${index}].duration_minutes`,
                                            offered.duration_minutes
                                          );
                                          form.setFieldValue(
                                            `services[${index}].price`,
                                            offered.price
                                          );
                                        }
                                      }}
                                    >
                                      <SelectTrigger
                                        id={field.name}
                                        aria-invalid={isInvalid || showConflict}
                                      >
                                        <SelectValue placeholder="Select..." />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {rowStylistOptions.map((member) => (
                                          <SelectItem
                                            key={member.id}
                                            value={String(member.id)}
                                          >
                                            {staffLabel(member)}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    {index === 0 && availabilityIsError && (
                                      <FieldError>
                                        Could not load availability
                                      </FieldError>
                                    )}
                                    {index === 0 && noStaffWorking && (
                                      <FieldError>
                                        No staff are working on this date
                                      </FieldError>
                                    )}
                                    {conflict === "stylist" && (
                                      <FieldError>
                                        {`${rowStylistName} isn't working on this date. Choose another stylist or another date.`}
                                      </FieldError>
                                    )}
                                    {isInvalid && (
                                      <FieldError
                                        errors={field.state.meta.errors}
                                      />
                                    )}
                                  </Field>
                                );
                              }}
                            />
                            <form.Field
                              name={`services[${index}].service_id`}
                              children={(field) => {
                                const isInvalid =
                                  field.state.meta.isTouched &&
                                  !field.state.meta.isValid;
                                const showConflict = conflict === "service";
                                return (
                                  <Field data-invalid={isInvalid || showConflict}>
                                    <FieldLabel htmlFor={field.name}>
                                      Service
                                      <RequiredInput />
                                    </FieldLabel>
                                    <Select
                                      key={selectedStaffId}
                                      disabled={selectedStaffId === 0}
                                      value={
                                        field.state.value
                                          ? String(field.state.value)
                                          : undefined
                                      }
                                      onValueChange={(value) => {
                                        const next = Number(value);
                                        if (next === field.state.value) return;
                                        const selected = rowServiceOptions.find(
                                          (service) =>
                                            service.service_id === next
                                        );
                                        if (
                                          selected?.duration_minutes == null ||
                                          selected.price == null
                                        ) {
                                          return;
                                        }
                                        field.handleChange(next);
                                        form.setFieldValue(
                                          `services[${index}].duration_minutes`,
                                          selected.duration_minutes
                                        );
                                        form.setFieldValue(
                                          `services[${index}].price`,
                                          selected.price
                                        );
                                      }}
                                    >
                                      <SelectTrigger
                                        id={field.name}
                                        aria-invalid={isInvalid || showConflict}
                                      >
                                        <SelectValue placeholder="Select..." />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {rowServiceOptions.map((service) => (
                                          <SelectItem
                                            key={service.service_id}
                                            value={String(service.service_id)}
                                          >
                                            {service.price == null
                                              ? service.service_name
                                              : `${service.service_name} · ${intl.formatNumber(service.price, { format: "money" })}`}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    {conflict === "service" && (
                                      <FieldError>
                                        {`${rowStylistName} doesn't do ${serviceNameFor(selectedServiceId, servicesSummary, appointment)}. Choose another stylist, or another service.`}
                                      </FieldError>
                                    )}
                                    {noServicesAssigned && (
                                      <FieldDescription>
                                        No services assigned for this stylist
                                      </FieldDescription>
                                    )}
                                    {isInvalid && (
                                      <FieldError
                                        errors={field.state.meta.errors}
                                      />
                                    )}
                                  </Field>
                                );
                              }}
                            />
                            <form.Field
                              name={`services[${index}].duration_minutes`}
                              children={(field) => {
                                const isInvalid =
                                  field.state.meta.isTouched &&
                                  !field.state.meta.isValid;
                                return (
                                  <Field data-invalid={isInvalid}>
                                    <FieldLabel htmlFor={field.name}>
                                      Duration
                                      <RequiredInput />
                                    </FieldLabel>
                                    <Select
                                      disabled={selectedServiceId === 0}
                                      value={
                                        field.state.value > 0
                                          ? String(field.state.value)
                                          : undefined
                                      }
                                      onValueChange={(value) => {
                                        field.handleChange(Number(value));
                                      }}
                                    >
                                      <SelectTrigger
                                        id={field.name}
                                        aria-invalid={isInvalid}
                                      >
                                        <SelectValue placeholder="Select..." />
                                      </SelectTrigger>
                                      <SelectContent className="max-h-60">
                                        {durationOptions.map((minutes) => (
                                          <SelectItem
                                            key={minutes}
                                            value={String(minutes)}
                                          >
                                            {intl.formatDuration(durationFromMinutes(minutes), { format: "short" })}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    {isInvalid && (
                                      <FieldError
                                        errors={field.state.meta.errors}
                                      />
                                    )}
                                  </Field>
                                );
                              }}
                            />
                            <form.Field
                              name={`services[${index}].start_time`}
                              children={(field) => {
                                const isInvalid =
                                  field.state.meta.isTouched &&
                                  !field.state.meta.isValid;
                                const showConflict =
                                  conflict === "time" ||
                                  conflict === "duration" ||
                                  conflict === "overlap";
                                return (
                                  <Field data-invalid={isInvalid || showConflict}>
                                    <FieldLabel htmlFor={field.name}>
                                      Time
                                      <RequiredInput />
                                    </FieldLabel>
                                    <Select
                                      key={`${selectedStaffId}-${formDateStr}-${selectedServiceId}`}
                                      disabled={selectedServiceId === 0}
                                      value={field.state.value || undefined}
                                      onValueChange={(value) => {
                                        field.handleChange(value);
                                      }}
                                    >
                                      <SelectTrigger
                                        id={field.name}
                                        aria-invalid={isInvalid || showConflict}
                                      >
                                        <SelectValue placeholder="Select..." />
                                      </SelectTrigger>
                                      <SelectContent className="max-h-60">
                                        {timeOptions.map((time) => (
                                          <SelectItem key={time} value={time}>
                                            {intl.formatTime(isoTime(time), { format: "clock" })}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    {conflict === "time" && (
                                      <FieldError>
                                        That time isn't available. Choose
                                        another time.
                                      </FieldError>
                                    )}
                                    {conflict === "duration" && (
                                      <FieldError>
                                        There isn't enough time at that slot.
                                        Choose another time or duration.
                                      </FieldError>
                                    )}
                                    {conflict === "overlap" && (
                                      <FieldError>
                                        This overlaps another service on this
                                        appointment. Change the time or
                                        duration.
                                      </FieldError>
                                    )}
                                    {noTimesAvailable && (
                                      <FieldDescription>
                                        {selectedServiceId !== 0
                                          ? "No available times for this duration"
                                          : "No available times for this stylist"}
                                      </FieldDescription>
                                    )}
                                    {isInvalid && (
                                      <FieldError
                                        errors={field.state.meta.errors}
                                      />
                                    )}
                                  </Field>
                                );
                              }}
                            />
                            {services.length > 1 ? (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="justify-self-end sm:mt-6 sm:justify-self-auto"
                                aria-label="Remove service"
                                onClick={() => linesField.removeValue(index)}
                              >
                                <X />
                              </Button>
                            ) : (
                              <div
                                className="mt-6 size-8 max-sm:hidden"
                                aria-hidden
                              />
                            )}
                          </div>
                        );
                      }}
                    />
                  ))}
                </>
              )}
            />

            <form.Subscribe
              selector={(state) => state.values.services}
              children={(services) => (
                <Field orientation="horizontal">
                  <FieldLabel>Total</FieldLabel>
                  <p className="text-sm font-medium">
                    {intl.formatNumber(sumLinePrices(services), { format: "money" })}
                  </p>
                </Field>
              )}
            />

            <Separator />

            <form.Field
              name="status"
              children={(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;
                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>
                      Status
                      <RequiredInput />
                    </FieldLabel>
                    <Select
                      value={field.state.value}
                      onValueChange={(value) => {
                        field.handleChange(
                          value as (typeof APPOINTMENT_CALENDAR_STATUS_OPTIONS)[number]["value"]
                        );
                      }}
                    >
                      <SelectTrigger id={field.name} aria-invalid={isInvalid}>
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        {APPOINTMENT_CALENDAR_STATUS_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {isInvalid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                  </Field>
                );
              }}
            />
          </FieldGroup>
        </form>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" className="w-24">
              Cancel
            </Button>
          </DialogClose>
          <form.Subscribe
            selector={(state) => state.values.services}
            children={(services) => (
              <Button
                type="submit"
                form="edit-appointment-form"
                className="w-24"
                disabled={
                  !availabilityReady ||
                  services.some((line, index) =>
                    Boolean(conflictForLine(line, index, services))
                  )
                }
              >
                Save
              </Button>
            )}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
